import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import {
  IActionDataModel,
  IAdaptiveActionCard,
  IQuestionAnswer,
  IAnsweredQuestionState,
} from '@ninjacart/nc-nk-core';
import { MMKVStoreKeyMap } from '../mmkv/mmkvStoreKeyMap';
import { isLessThanTenMinutesAgo } from '../utils/utils';
import { zustandQnaFeedStore } from '../mmkv/mmkvQnAStore';

type QnaFeedStore = {
  qnaFeed: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[];
  userQnaFeed: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[];
  commentReplyExpanded: boolean;
  setCommentReplyExpanded: (isExpanded: boolean) => void;
  setUserQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[]) => void;
  setQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[]) => void;
  addUserQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>) => void;
  addQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>) => void;
  setHideTimers: (canHide: boolean) => void;
  hideTimer: boolean;
  showTimer: boolean;
  setShowTimer: (val: boolean) => void;
  questionActiveTimerList: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[];
  expertAnswerImmediate?: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>;
  setExpertAnswerImmediate: () => void;
  setQuestionActiveTimerList: () => void;
  removeQuestionActiveTimer: (item: IQuestionAnswer, interval?: any) => void;
  addQuestionActiveTimer: (item: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>) => void;
  isCommentInputHidden: boolean;
  setCommentInputHidden: (hidden: boolean) => void;
  isAskQuestionDetailPage: boolean;
  setisAskQuestionDetailPage: (isAskQuestionDetailPage: boolean) => void;
  getQnaInput: string;
  setQnaInput: (getQnaInput: string) => void;

  hasQuestionAnswered: {
    [deeplinkQuestionId: string]: IAnsweredQuestionState;
  };

  setHasQuestionAnswered: (
    deeplinkQuestionId: string,
    key: keyof IAnsweredQuestionState,
    isActive: boolean
  ) => void;
  removeViewedQuestionId: (id: string) => void;

  lastAnsweredQuestion: string;
  setLastAnsweredQuestion: (id: string) => void;
  hideQnaBanner: boolean;
  setHideQnaBanner: (hideQnaBanner: boolean) => void;
  languageFilterName: string;
  setLanguageFilterName: (languageFilterName: string) => void;
  languageFilterValue: string;
  setLanguageFilterValue: (languageFilterValue: string) => void;
};

export const useQnaFeedStore = create<QnaFeedStore>()(
  persist(
    (set, get) => ({
      qnaFeed: [],
      isCommentInputHidden: false,
      userQnaFeed: [],
      hideTimer: true,
      questionActiveTimerList: [],
      expertAnswerImmediate: undefined,
      isAskQuestionDetailPage: false,
      setExpertAnswerImmediate: () => {
        const items = get()?.userQnaFeed.slice(0, 2);
        for (const item of items) {
          const child =
            get()?.qnaFeed?.find(
              (x) => x.actionData?.content?.id === item.actionData?.content?.id
            ) ?? item;
          const TEN_MINUTES = 10 * 60 * 1000;
          const duration = Date.now() - child?.actionData?.content?.timestamp;

          if (duration < TEN_MINUTES) {
            const hasExpertAnswer = child?.actionData?.content?.children?.find(
              (x) => x.creatorRole === 'mods'
            );
            if (hasExpertAnswer) {
              set(() => ({ expertAnswerImmediate: child }));
              break;
            }
          }
        }
      },
      setCommentInputHidden: (hidden: boolean) => set(() => ({ isCommentInputHidden: hidden })),
      setQuestionActiveTimerList: () => {
        set(() => ({ questionActiveTimerList: [] }));
        const items: any[] = [];
        const feeds = get()?.userQnaFeed;
        for (let i = 0; i < feeds?.length; i++) {
          const isLessThan = isLessThanTenMinutesAgo(feeds[i].actionData?.content?.timestamp ?? 0);

          if (isLessThan > -1) {
            const item =
              get()?.qnaFeed.find(
                (x) => x.actionData?.content?.id === feeds[i].actionData?.content?.id
              ) ?? feeds[i];
            const hasExpertAnswer = item?.actionData?.content?.children?.find(
              (x) => x.creatorRole === 'mods'
            );
            if (hasExpertAnswer) {
              console.log('has expert');
              continue;
            }

            items.push(item);
            console.log('Has expert answer for timer => ', items?.length);
          }
        }

        set(() => ({ questionActiveTimerList: items }));
      },
      addQuestionActiveTimer: (item: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>) => {
        set((state) => ({ questionActiveTimerList: [...state.questionActiveTimerList, item] }));
      },
      removeQuestionActiveTimer: (item: IQuestionAnswer, interval?: any) => {
        if (interval) {
          console.log('Clearing interval for item => ', item);
          clearInterval(interval);
        }

        set((state) => ({
          questionActiveTimerList: state.questionActiveTimerList.filter(
            (x) => x.actionData?.content.id !== item.id
          ),
        }));
      },
      setHideTimers: (hide: boolean) => set(() => ({ hideTimer: hide })),
      commentReplyExpanded: false,
      setCommentReplyExpanded: (isExpanded: boolean) =>
        set(() => ({ commentReplyExpanded: isExpanded })),
      setQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[]) =>
        set(() => ({ qnaFeed: payload })),
      showTimer: false,
      setShowTimer: (val: boolean) => set((state) => ({ showTimer: val })),
      setUserQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[]) => {
        set((state) => ({ userQnaFeed: payload }));
        get()?.setQuestionActiveTimerList();
        get()?.setExpertAnswerImmediate();
      },
      addUserQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>) => {
        set((state) => ({ userQnaFeed: [payload, ...state.userQnaFeed] }));
        get()?.setQuestionActiveTimerList();
      },
      addQnaFeed: (payload: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>) => {
        set((state) => ({ qnaFeed: [payload, ...state.qnaFeed] }));
      },
      setisAskQuestionDetailPage: (hidden: boolean) =>
        set(() => ({ isAskQuestionDetailPage: hidden })),
      getQnaInput: '',
      setQnaInput: (getQnaInput: string) => set(() => ({ getQnaInput })),

      hasQuestionAnswered: {},

      setHasQuestionAnswered: (deeplinkQuestionId, key, isActive) => {
        if (!deeplinkQuestionId) return;
        set((state) => ({
          hasQuestionAnswered: {
            ...state.hasQuestionAnswered,
            [deeplinkQuestionId]: {
              ...state.hasQuestionAnswered[deeplinkQuestionId],
              [key]: isActive,
            },
          },
        }));
      },

      removeViewedQuestionId: (id: string) => {
        set((state) => {
          const { [id]: _, ...remainingQuestions } = state.hasQuestionAnswered;
          return {
            hasQuestionAnswered: remainingQuestions,
          };
        });
      },
      lastAnsweredQuestion: '',
      setLastAnsweredQuestion: (id: string) => set(() => ({ lastAnsweredQuestion: id })),
      hideQnaBanner: false,
      setHideQnaBanner: (hideQnaBanner: boolean) => set(() => ({ hideQnaBanner })),
      languageFilterName: '',
      setLanguageFilterName: (languageFilterName: string) => set(() => ({ languageFilterName })),
      languageFilterValue: '',
      setLanguageFilterValue: (languageFilterValue: string) => set(() => ({ languageFilterValue })),
    }),
    {
      name: MMKVStoreKeyMap.qnaFeedStore,
      storage: createJSONStorage(() => zustandQnaFeedStore),
      partialize: (state) => ({
        hasQuestionAnswered: state?.hasQuestionAnswered,
        lastAnsweredQuestion: state?.lastAnsweredQuestion,
        removeViewedQuestionId: state?.removeViewedQuestionId,
        hideQnaBanner: state?.hideQnaBanner,
        languageFilterName: state?.languageFilterName,
      }),
    }
  )
);
